import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DadosGraficoPizza } from '../../../models/dashboard/dre.model';
import { MOEDA_OPTIONS, MOEDA_CONFIG } from '../../../enums/moeda.enum';

interface SelectItem { label: string; value: any; }

// Paleta exata do mobile (conta_controller.dart getCor())
const PALETA_CORES = [
  '#4F81BD', '#F79646', '#9BBB59', '#C0504D',
  '#8064A2', '#FFCC00', '#4BACC6', '#A5A5A5'
];

@Component({
  selector: 'app-dashboard-grafico-pizza',
  templateUrl: './dashboard-grafico-pizza.component.html',
  styleUrls: ['./dashboard-grafico-pizza.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardGraficoPizzaComponent implements OnInit, OnDestroy, OnChanges {

  @Input() dadosDespesas: DadosGraficoPizza[] = [];
  @Input() dadosReceitas: DadosGraficoPizza[] = [];
  @Input() totalDespesas = 0;
  @Input() totalReceitas = 0;
  @Input() loading = false;
  @Input() currencySymbol = 'PYG';
  @Input() currencyDigits = '1.0-0';
  @Input() currencyLocale = 'es-PY';

  @Input() set moedaInicial(valor: string | undefined) {
    if (valor) {
      this.moedaPizza = valor;
    }
  }

  @Output() filtroChanged = new EventEmitter<{ ano: number; mes: number | null; moeda: string }>();
  // Emite quando usuario clica num item para drill-down (replica grafico_pizza_widget.dart onTap)
  @Output() itemDespesaClicado = new EventEmitter<{ tipoEnum: string; nome: string; dataInicial: string; dataFinal: string; moeda: string }>();
  @Output() itemReceitaClicado = new EventEmitter<{ tipoEnum: string; nome: string; dataInicial: string; dataFinal: string; moeda: string }>();

  texts = {
    dados_de_movimentacoes: '',
    despesas: '',
    receitas: '',
    total: '',
    sem_dados: '',
    ano_completo: ''
  };

  modoGrafico = true;
  anoPizza: number = new Date().getFullYear();
  mesPizza: number | null = new Date().getMonth() + 1;
  moedaPizza = 'GUARANI';

  // Getters de formatacao derivados da moeda selecionada no filtro interno
  // Garante que o template use sempre a moeda correta, independente do @Input do pai
  get pizzaCurrencySymbol(): string {
    return MOEDA_CONFIG[this.moedaPizza as keyof typeof MOEDA_CONFIG]?.symbol || 'PYG';
  }

  get pizzaCurrencyDigits(): string {
    return MOEDA_CONFIG[this.moedaPizza as keyof typeof MOEDA_CONFIG]?.digits || '1.0-0';
  }

  get pizzaCurrencyLocale(): string {
    return MOEDA_CONFIG[this.moedaPizza as keyof typeof MOEDA_CONFIG]?.locale || 'es-PY';
  }

  anos: SelectItem[] = [];
  meses: SelectItem[] = [];
  moedas: SelectItem[] = MOEDA_OPTIONS;

  pieDespesaData: any;
  pieDespesaOptions: any;
  pieReceitaData: any;
  pieReceitaOptions: any;

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.cdr.markForCheck();
    });
    this.buildOptions();
    this.buildCharts();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dadosDespesas'] || changes['dadosReceitas']) {
      this.buildCharts();
      this.cdr.markForCheck();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      dados_de_movimentacoes: this.translate.instant('dashboard.texts.dados_de_movimentacoes'),
      despesas: this.translate.instant('dashboard.texts.despesas'),
      receitas: this.translate.instant('dashboard.texts.receitas'),
      total: this.translate.instant('dashboard.texts.total'),
      sem_dados: this.translate.instant('dashboard.texts.sem_dados'),
      ano_completo: this.translate.instant('dashboard.texts.ano_completo')
    };
  }

  private buildOptions(): void {
    const currentYear = new Date().getFullYear();
    this.anos = [];
    for (let y = currentYear; y >= 2000; y--) {
      this.anos.push({ label: String(y), value: y });
    }

    this.meses = [
      { label: this.translate.instant('dashboard.texts.ano_completo') || 'Ano completo', value: null },
      { label: 'Jan', value: 1 }, { label: 'Fev', value: 2 }, { label: 'Mar', value: 3 },
      { label: 'Abr', value: 4 }, { label: 'Mai', value: 5 }, { label: 'Jun', value: 6 },
      { label: 'Jul', value: 7 }, { label: 'Ago', value: 8 }, { label: 'Set', value: 9 },
      { label: 'Out', value: 10 }, { label: 'Nov', value: 11 }, { label: 'Dez', value: 12 }
    ];
  }

  onFiltroChange(): void {
    this.filtroChanged.emit({
      ano: this.anoPizza,
      mes: this.mesPizza,
      moeda: this.moedaPizza
    });
  }

  toggleModo(): void {
    this.modoGrafico = !this.modoGrafico;
  }

  // Drill-down: ao clicar num item de despesa navega para movimentacoes filtrada
  // Replica grafico_pizza_widget.dart onTap com tipoDespesa
  onItemDespesaClick(item: DadosGraficoPizza): void {
    if (item.tipoEnum === 'OUTROS') return; // "Outros" nao tem drill-down
    const datas = this.calcularDatasParaFiltro();
    this.itemDespesaClicado.emit({
      tipoEnum: item.tipoEnum,
      nome: item.nome,
      dataInicial: datas.dataInicial,
      dataFinal: datas.dataFinal,
      moeda: this.moedaPizza
    });
  }

  // Drill-down: ao clicar num item de receita navega para movimentacoes filtrada
  // Replica grafico_pizza_widget.dart onTap com tipoReceita
  onItemReceitaClick(item: DadosGraficoPizza): void {
    if (item.tipoEnum === 'OUTROS') return; // "Outros" nao tem drill-down
    const datas = this.calcularDatasParaFiltro();
    this.itemReceitaClicado.emit({
      tipoEnum: item.tipoEnum,
      nome: item.nome,
      dataInicial: datas.dataInicial,
      dataFinal: datas.dataFinal,
      moeda: this.moedaPizza
    });
  }

  private pad(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }

  private calcularDatasParaFiltro(): { dataInicial: string; dataFinal: string } {
    if (this.mesPizza && this.mesPizza >= 1 && this.mesPizza <= 12) {
      const ultimoDia = new Date(this.anoPizza, this.mesPizza, 0).getDate();
      return {
        dataInicial: `01/${this.pad(this.mesPizza)}/${this.anoPizza}`,
        dataFinal: `${this.pad(ultimoDia)}/${this.pad(this.mesPizza)}/${this.anoPizza}`
      };
    }
    return {
      dataInicial: `01/01/${this.anoPizza}`,
      dataFinal: `31/12/${this.anoPizza}`
    };
  }

  private buildCharts(): void {
    this.pieDespesaData = this.buildPieData(this.dadosDespesas);
    this.pieDespesaOptions = this.buildPieOptions();
    this.pieReceitaData = this.buildPieData(this.dadosReceitas);
    this.pieReceitaOptions = this.buildPieOptions();
  }

  private buildPieData(dados: DadosGraficoPizza[]): any {
    if (!dados || dados.length === 0) {
      return {
        labels: [this.texts.sem_dados || 'Sem dados'],
        datasets: [{ data: [1], backgroundColor: ['#e0e0e0'] }]
      };
    }

    return {
      labels: dados.map(d => d.nome),
      datasets: [{
        data: dados.map(d => d.valor),
        backgroundColor: dados.map((d, i) => d.cor || PALETA_CORES[i % PALETA_CORES.length]),
        borderWidth: 0.5,
        borderColor: '#ffffff'
      }]
    };
  }

  // Plugin inline do Chart.js para desenhar porcentagem dentro das fatias
  piePercentPlugin = {
    id: 'piePercentLabels',
    afterDraw: (chart: any) => {
      const { ctx } = chart;
      const dataset = chart.data.datasets[0];
      if (!dataset || !dataset.data) return;

      const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
      if (total === 0) return;

      const meta = chart.getDatasetMeta(0);
      if (!meta || !meta.data) return;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#fff';

      meta.data.forEach((arc: any, i: number) => {
        const valor = dataset.data[i];
        const percentual = (valor / total) * 100;

        // So exibir label se a fatia for grande o suficiente (>= 5%)
        if (percentual < 5) return;

        const { x, y } = arc.tooltipPosition();
        const texto = `${percentual.toFixed(1)}%`;

        // Sombra para legibilidade sobre cores claras
        ctx.shadowColor = 'rgba(0,0,0,0.4)';
        ctx.shadowBlur = 3;
        ctx.fillText(texto, x, y);
        ctx.shadowBlur = 0;
      });

      ctx.restore();
    }
  };

  private buildPieOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.label || '';
              const value = this.formatarValorMoeda(ctx.parsed || 0);
              const total = ctx.dataset.data.reduce((s: number, v: number) => s + v, 0);
              const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0.0';
              return `${label}: ${value} (${pct}%)`;
            }
          }
        }
      }
    };
  }

  // Formata valor monetario usando configuracao da moeda selecionada no filtro
  private formatarValorMoeda(valor: number): string {
    const config = MOEDA_CONFIG[this.moedaPizza as keyof typeof MOEDA_CONFIG] || MOEDA_CONFIG['GUARANI'];
    const casasDecimais = config.digits === '1.0-0' ? 0 : 2;
    return valor.toLocaleString(config.locale, {
      minimumFractionDigits: casasDecimais,
      maximumFractionDigits: casasDecimais
    });
  }

  getCorItem(index: number): string {
    return PALETA_CORES[index % PALETA_CORES.length];
  }

  trackByTipoEnum(index: number, item: DadosGraficoPizza): string {
    return item.tipoEnum;
  }
}
